// Audio edits on raw mono samples (Float32Array, -1..1).
//
// The browser editor (src/utils/audioEdit.js) wraps these in AudioBuffers; the
// server-side task runner (tasks/audio.js) feeds them PCM decoded by ffmpeg.
// One implementation, so a splice made by the Autoexecutor sounds exactly like
// one made by hand in the studio: same crossfade, same cut arithmetic, same
// WAV encoding.

const clampSamp = (len, n) => Math.max(0, Math.min(len, n));
const sec2samp = (len, sr, t) => clampSamp(len, Math.round(t * sr));

// Replace d[a..b) (sample indices) with `p`, overlapping both seams by `f`
// samples so neither one clicks. A cut placed on a true zero crossing is rare;
// a few milliseconds of crossfade makes the join inaudible even when the
// boundary is a little off. f 0 gives a plain butt splice.
// -> {out, fade}: the new samples and the fade actually used (it is clamped to
// what the three parts can lend: the head, the tail and half the piece).
function spliceCrossfadeSamples(d, a, b, p, f) {
  const head = d.subarray(0, a);
  const tail = d.subarray(b);
  const fade = Math.max(0, Math.min(f || 0, head.length, tail.length, Math.floor(p.length / 2)));
  const out = new Float32Array(head.length + p.length + tail.length - 2 * fade);
  out.set(head.subarray(0, head.length - fade), 0);
  let w = head.length - fade;
  for (let i = 0; i < fade; i += 1) {          // head fades out under the piece
    const t = (i + 1) / (fade + 1);
    out[w + i] = head[head.length - fade + i] * (1 - t) + p[i] * t;
  }
  w += fade;
  out.set(p.subarray(fade, p.length - fade), w);
  w += p.length - 2 * fade;
  for (let i = 0; i < fade; i += 1) {          // piece fades out under the tail
    const t = (i + 1) / (fade + 1);
    out[w + i] = p[p.length - fade + i] * (1 - t) + tail[i] * t;
  }
  w += fade;
  out.set(tail.subarray(fade), w);
  return { out, fade };
}

// Seconds-based front for the above: d sampled at `sr`.
// -> {out, fadeSec}
function spliceCrossfade(d, sr, outStart, outEnd, p, fadeSec) {
  const a = sec2samp(d.length, sr, outStart);
  const b = sec2samp(d.length, sr, outEnd);
  const { out, fade } = spliceCrossfadeSamples(d, a, b, p, Math.round((fadeSec || 0) * sr));
  return { out, fadeSec: fade / sr };
}

// Several spans dropped in one pass. Overlaps are merged; order does not matter.
function deleteRegions(d, sr, spans) {
  const ranges = spans
    .map(({ start, end }) => [sec2samp(d.length, sr, start), sec2samp(d.length, sr, end)])
    .filter(([s, e]) => e > s)
    .sort((x, y) => x[0] - y[0]);
  if (!ranges.length) return d;
  const kept = [];
  let from = 0;
  for (const [s, e] of ranges) {
    if (s > from) kept.push(d.subarray(from, s));
    from = Math.max(from, e);
  }
  if (from < d.length) kept.push(d.subarray(from));
  const out = new Float32Array(kept.reduce((n, k) => n + k.length, 0));
  let at = 0;
  for (const k of kept) { out.set(k, at); at += k.length; }
  return out;
}

// Only [start, end] seconds kept.
function trimTo(d, sr, start, end) {
  const s = sec2samp(d.length, sr, start);
  const e = sec2samp(d.length, sr, end);
  if (e <= s) return d;
  return d.slice(s, e);
}

// RIFF/WAVE, PCM, mono, 16-bit. -> Uint8Array (a Node Buffer wraps it as is).
function encodeWav16(channel, sampleRate) {
  const len = channel.length;
  const dataLen = len * 2;
  const buffer = new ArrayBuffer(44 + dataLen);
  const view = new DataView(buffer);
  const writeStr = (off, s) => { for (let i = 0; i < s.length; i++) view.setUint8(off + i, s.charCodeAt(i)); };
  writeStr(0, 'RIFF');
  view.setUint32(4, 36 + dataLen, true);
  writeStr(8, 'WAVE');
  writeStr(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);            // PCM
  view.setUint16(22, 1, true);            // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);            // block align
  view.setUint16(34, 16, true);           // bits per sample
  writeStr(36, 'data');
  view.setUint32(40, dataLen, true);
  let off = 44;
  for (let i = 0; i < len; i++) {
    let s = Math.max(-1, Math.min(1, channel[i] || 0));
    s = s < 0 ? s * 0x8000 : s * 0x7FFF;
    view.setInt16(off, s, true);
    off += 2;
  }
  return new Uint8Array(buffer);
}

// Peak level of [start, end] seconds — a sanity check that a piece is not silence.
function peakOf(d, sr, start = 0, end = Infinity) {
  const s = sec2samp(d.length, sr, start);
  const e = Number.isFinite(end) ? sec2samp(d.length, sr, end) : d.length;
  let m = 0;
  for (let i = s; i < e; i += 1) { const v = Math.abs(d[i]); if (v > m) m = v; }
  return m;
}

module.exports = { sec2samp, spliceCrossfadeSamples, spliceCrossfade, deleteRegions, trimTo, encodeWav16, peakOf };
